import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Checkbox,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  File,
  ChevronRight,
  Home,
  Search,
  Archive,
  HardDrive,
  FolderPlus,
  Laptop,
} from 'lucide-react'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import api from '../services/api'
import { managedAgentsAPI, rcloneAPI, sshKeysAPI } from '../services/api'
import { useTranslation } from 'react-i18next'

interface FileSystemItem {
  name: string
  path: string
  is_directory: boolean
  size?: number
  modified?: string
  is_borg_repo: boolean
  is_local_mount?: boolean // Local host filesystem mount
  is_mount_point?: boolean // SSH mount point
  ssh_connection?: SSHConnection
  permissions?: string
}

interface SSHConnection {
  id: number
  ssh_key_id: number
  host: string
  username: string
  port: number
  default_path?: string
  mount_point?: string
  status: string
}

interface SSHNetworkConfig {
  ssh_key_id: number
  host: string
  username: string
  port: number
}

type FileExplorerConnectionType = 'local' | 'ssh' | 'agent' | 'rclone'

interface BrowseCacheEntry {
  currentPath: string
  items: FileSystemItem[]
  isInsideLocalMount: boolean
}

function getAgentBrowseCacheKey(agentId: number, path: string, includeHidden: boolean) {
  return `agent:${agentId}:${includeHidden ? 'hidden' : 'visible'}:${path || '/'}`
}

function resolveInitialBrowsePath(
  connectionType: FileExplorerConnectionType,
  initialPath: string,
  agentDefaultPath?: string | null
) {
  const trimmedAgentDefaultPath = agentDefaultPath?.trim()
  if (
    connectionType === 'agent' &&
    trimmedAgentDefaultPath &&
    (!initialPath || initialPath === '/')
  ) {
    return trimmedAgentDefaultPath
  }
  return initialPath
}

function normalizeRcloneBrowsePath(path: string) {
  if (!path || path === '/') return ''
  return path.replace(/^\/+/, '')
}

interface FileExplorerDialogProps {
  open: boolean
  onClose: () => void
  onSelect: (selectedPaths: string[]) => void
  title?: string
  initialPath?: string
  multiSelect?: boolean
  connectionType?: FileExplorerConnectionType
  agentId?: number
  agentName?: string
  agentDefaultPath?: string | null
  rcloneRemoteId?: number | null
  sshConfig?: SSHNetworkConfig
  selectMode?: 'directories' | 'files' | 'both'
  showSshMountPoints?: boolean // Set to false to hide SSH mount points (e.g., when repo is SSH to prevent remote-to-remote)
  allowedSshConnectionId?: number | null // Only show this SSH connection's mount point (used when source_connection_id is already set)
  dialogContainer?: HTMLElement | (() => HTMLElement | null) | null
}

export default function FileExplorerDialog({
  open,
  onClose,
  onSelect,
  title,
  initialPath = '/',
  multiSelect = false,
  connectionType = 'local',
  agentId,
  agentName,
  agentDefaultPath,
  rcloneRemoteId,
  sshConfig,
  selectMode = 'directories',
  showSshMountPoints = true,
  allowedSshConnectionId = null,
  dialogContainer,
}: FileExplorerDialogProps) {
  const { t } = useTranslation()
  const [currentPath, setCurrentPath] = useState(initialPath)
  const [items, setItems] = useState<FileSystemItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedPaths, setSelectedPaths] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [sshConnections, setSshConnections] = useState<SSHConnection[]>([])
  const [shouldFocusSearchInput, setShouldFocusSearchInput] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Track current browsing mode (can switch from local to ssh when clicking mount points)
  const [activeConnectionType, setActiveConnectionType] = useState(connectionType)
  const [activeSshConfig, setActiveSshConfig] = useState(sshConfig)
  const [isInsideLocalMount, setIsInsideLocalMount] = useState(false)

  // Create folder dialog
  const [showCreateFolder, setShowCreateFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [creatingFolder, setCreatingFolder] = useState(false)

  // Track if initial load has been done to prevent re-triggering
  const initialLoadDone = useRef(false)
  const browseCache = useRef(new Map<string, BrowseCacheEntry>())

  // Responsive dialog
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  const loadSSHConnections = async () => {
    try {
      const response = await sshKeysAPI.getSSHConnections()
      const connections = response.data?.connections || []
      // Show all connected SSH connections (mount point is optional)
      setSshConnections(connections.filter((conn: SSHConnection) => conn.status === 'connected'))
    } catch (err) {
      // Silently fail - mount points are optional
      console.error('Failed to load SSH connections:', err)
      setSshConnections([])
    }
  }

  const loadDirectory = React.useCallback(
    async (path: string, conn?: FileExplorerConnectionType, config?: SSHNetworkConfig) => {
      setError(null)

      // Update state if new connection params provided
      if (conn !== undefined) {
        setActiveConnectionType(conn)
      }
      if (config !== undefined) {
        setActiveSshConfig(config)
      }

      const useConnectionType = conn !== undefined ? conn : activeConnectionType
      const useSshConfig = config !== undefined ? config : activeSshConfig
      const requestedPath = path || '/'
      const includeHidden = false

      try {
        if (useConnectionType === 'agent') {
          if (!agentId) {
            setError(t('fileExplorer.selectAgentFirst'))
            setItems([])
            return
          }

          const cachedEntry = browseCache.current.get(
            getAgentBrowseCacheKey(agentId, requestedPath, includeHidden)
          )
          if (cachedEntry) {
            setItems(cachedEntry.items)
            setCurrentPath(cachedEntry.currentPath)
            setIsInsideLocalMount(cachedEntry.isInsideLocalMount)
            return
          }

          setLoading(true)
          const response = await managedAgentsAPI.browseFilesystem(
            agentId,
            requestedPath,
            includeHidden
          )
          const agentItems = (response.data.items || []).map((item) => ({
            name: item.name,
            path: item.path,
            is_directory: item.type === 'directory',
            size: item.size,
            modified: item.modified_at
              ? new Date(item.modified_at * 1000).toISOString()
              : undefined,
            is_borg_repo: false,
            permissions: item.hidden ? 'hidden' : undefined,
          }))
          const currentAgentPath = response.data.current_path || requestedPath
          const entry = {
            currentPath: currentAgentPath,
            items: agentItems,
            isInsideLocalMount: false,
          }
          browseCache.current.set(
            getAgentBrowseCacheKey(agentId, requestedPath, includeHidden),
            entry
          )
          browseCache.current.set(
            getAgentBrowseCacheKey(agentId, currentAgentPath, includeHidden),
            entry
          )
          setItems(agentItems)
          setCurrentPath(currentAgentPath)
          setIsInsideLocalMount(false)
          return
        }

        if (useConnectionType === 'rclone') {
          if (!rcloneRemoteId) {
            setError(t('fileExplorer.selectRemoteFirst'))
            setItems([])
            return
          }

          const rclonePath = normalizeRcloneBrowsePath(requestedPath)
          setLoading(true)
          const response = await rcloneAPI.browseRemote(rcloneRemoteId, rclonePath)
          const currentRclonePath = normalizeRcloneBrowsePath(response.data.path || rclonePath)
          setItems(
            (response.data.entries || []).map(
              (entry: { name: string; path: string; is_dir?: boolean }) => ({
                name: entry.name,
                path: normalizeRcloneBrowsePath(entry.path || entry.name),
                is_directory: Boolean(entry.is_dir),
                is_borg_repo: false,
              })
            )
          )
          setCurrentPath(currentRclonePath)
          setIsInsideLocalMount(false)
          return
        }

        setLoading(true)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const params: any = {
          path,
          connection_type: useConnectionType,
        }

        if (useConnectionType === 'ssh' && useSshConfig) {
          params.ssh_key_id = useSshConfig.ssh_key_id
          params.host = useSshConfig.host
          params.username = useSshConfig.username
          params.port = useSshConfig.port
        }

        const response = await api.get('/filesystem/browse', { params })
        setItems(response.data.items || [])
        setCurrentPath(response.data.current_path)
        setIsInsideLocalMount(response.data.is_inside_local_mount || false)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        if (useConnectionType === 'rclone') {
          setError(t('wizard.cloudMirror.browseFailed'))
          setItems([])
          return
        }

        const detail = err.response?.data?.detail
        if (detail && typeof detail === 'object' && detail.key) {
          setError(t(detail.key, detail.params) as string)
        } else {
          setError(typeof detail === 'string' ? detail : t('fileExplorer.failedToLoad'))
        }
        setItems([])
      } finally {
        setLoading(false)
      }
    },
    [activeConnectionType, activeSshConfig, agentId, rcloneRemoteId, t]
  )

  // Initial load - only runs once when dialog opens
  useEffect(() => {
    if (open && !initialLoadDone.current) {
      initialLoadDone.current = true
      // Reset selection state when dialog opens
      setSelectedPaths([])
      setSearchTerm('')

      const startPath = resolveInitialBrowsePath(connectionType, initialPath, agentDefaultPath)
      if (startPath) {
        // Use provided configuration for initial load
        loadDirectory(startPath, connectionType, sshConfig)
      } else {
        // Load default/root path
        loadDirectory('', connectionType, sshConfig)
      }

      // Load SSH connections to show mount points
      if (connectionType === 'local') {
        loadSSHConnections()
      }
    }
    // Reset when dialog closes
    if (!open) {
      initialLoadDone.current = false
      browseCache.current.clear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPath, connectionType, sshConfig, agentDefaultPath])

  useEffect(() => {
    browseCache.current.clear()
  }, [agentId, connectionType, agentDefaultPath, rcloneRemoteId])

  useEffect(() => {
    if (!shouldFocusSearchInput) return

    const timeoutId = window.setTimeout(() => {
      searchInputRef.current?.focus()
      setShouldFocusSearchInput(false)
    }, 100)

    return () => window.clearTimeout(timeoutId)
  }, [shouldFocusSearchInput])

  const handleItemSelect = (item: FileSystemItem) => {
    // Check if item type matches selectMode
    if (selectMode === 'directories' && !item.is_directory) return
    if (selectMode === 'files' && item.is_directory) return

    if (multiSelect) {
      setSelectedPaths((prev) =>
        prev.includes(item.path) ? prev.filter((p) => p !== item.path) : [...prev, item.path]
      )
    } else {
      setSelectedPaths([item.path])
    }
  }

  const handleBreadcrumbClick = (path: string) => {
    // If clicking root while in SSH mode from mount point, go back to local
    if (path === '/' && activeConnectionType === 'ssh' && connectionType === 'local') {
      setActiveConnectionType('local')
      setActiveSshConfig(undefined)
      loadDirectory('/', 'local', undefined)
    } else if (activeConnectionType === 'rclone') {
      loadDirectory(normalizeRcloneBrowsePath(path))
    } else {
      loadDirectory(path)
    }
  }

  const handleConfirm = () => {
    // Convert paths to SSH URL format if browsing via mount point
    const paths = selectedPaths.map((path) => {
      if (activeConnectionType === 'ssh' && activeSshConfig && connectionType === 'local') {
        // We're browsing a mount point - convert to SSH URL
        return `ssh://${activeSshConfig.username}@${activeSshConfig.host}:${activeSshConfig.port}${path}`
      }
      return path
    })
    onSelect(paths)
    onClose()
  }

  const handleSelectCurrent = () => {
    // Convert path to SSH URL format if browsing via mount point
    let path = currentPath
    if (activeConnectionType === 'ssh' && activeSshConfig && connectionType === 'local') {
      // We're browsing a mount point - convert to SSH URL
      path = `ssh://${activeSshConfig.username}@${activeSshConfig.host}:${activeSshConfig.port}${currentPath}`
    } else if (activeConnectionType === 'rclone') {
      path = normalizeRcloneBrowsePath(path)
    }
    onSelect([path])
    onClose()
  }

  const buildChildPath = (parentPath: string, childName: string) => {
    const cleanedChildName = childName.trim().replace(/\//g, '').replace(/\.\./g, '')
    if (!parentPath || parentPath === '/') return `/${cleanedChildName}`
    return `${parentPath.replace(/\/+$/, '')}/${cleanedChildName}`
  }

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return

    setCreatingFolder(true)
    try {
      const folderName = newFolderName.trim()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const params: any = {
        path: currentPath,
        folder_name: folderName,
        connection_type: activeConnectionType,
      }

      if (activeConnectionType === 'ssh' && activeSshConfig) {
        params.ssh_key_id = activeSshConfig.ssh_key_id
        params.host = activeSshConfig.host
        params.username = activeSshConfig.username
        params.port = activeSshConfig.port
      }

      const response = await api.post('/filesystem/create-folder', params)
      const createdPath = response.data?.path || buildChildPath(currentPath, folderName)

      await loadDirectory(createdPath)

      setShowCreateFolder(false)
      setNewFolderName('')
      setError(null)
      setSearchTerm('')
      setSelectedPaths([])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error('Failed to create folder:', err)
      const errorMessage = err.response?.data?.detail || t('fileExplorer.createFailed')
      // Handle validation errors from FastAPI
      if (typeof errorMessage === 'object') {
        setError(JSON.stringify(errorMessage))
      } else {
        setError(errorMessage)
      }
    } finally {
      setCreatingFolder(false)
    }
  }

  const getBreadcrumbs = () => {
    const parts = currentPath.split('/').filter(Boolean)
    const breadcrumbs: { label: string; path: string }[] = [
      { label: t('dialogs.fileExplorer.root'), path: '/' },
    ]

    let accumulatedPath = ''
    parts.forEach((part) => {
      accumulatedPath += `/${part}`
      breadcrumbs.push({ label: part, path: accumulatedPath })
    })

    return breadcrumbs
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-'
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let size = bytes
    let unitIndex = 0
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024
      unitIndex++
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`
  }

  // Add mount points as virtual items at root level
  const getMountPointItems = (): FileSystemItem[] => {
    if (currentPath !== '/' || activeConnectionType !== 'local' || !showSshMountPoints) return []

    // Filter connections based on allowedSshConnectionId
    const filteredConnections = allowedSshConnectionId
      ? sshConnections.filter((conn) => conn.id === allowedSshConnectionId)
      : sshConnections

    return filteredConnections.map((conn) => {
      // Use mount_point name if available, otherwise show full SSH URL
      const displayName =
        conn.mount_point && conn.mount_point.trim()
          ? conn.mount_point
          : `ssh://${conn.username}@${conn.host}:${conn.port}${conn.default_path || '/'}`

      return {
        name: displayName,
        path: `ssh://${conn.username}@${conn.host}:${conn.port}${conn.default_path || '/'}`,
        is_directory: true,
        is_borg_repo: false,
        is_mount_point: true,
        ssh_connection: conn,
      }
    })
  }

  // When allowedSshConnectionId is set and we're at root in local mode,
  // hide local filesystem items - only show the SSH mount point
  const shouldHideLocalItems =
    allowedSshConnectionId && currentPath === '/' && activeConnectionType === 'local'

  const allItems = [...getMountPointItems(), ...(shouldHideLocalItems ? [] : items)]
  const filteredItems = allItems.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  )
  const agentLabel =
    agentName?.trim() ||
    (agentId ? t('backupPlans.sourceChooser.agentFallback', { id: agentId }) : '')

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
        container={dialogContainer}
        slotProps={{
          paper: { sx: { height: fullScreen ? '100%' : '75vh' } },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
              }}
            >
              {title ?? t('dialogs.fileExplorer.selectDirectory')}
            </Typography>
            {activeConnectionType === 'agent' && agentId ? (
              <Chip
                icon={<Laptop size={14} />}
                label={agentLabel}
                size="small"
                color="primary"
                variant="outlined"
              />
            ) : activeConnectionType === 'ssh' && activeSshConfig ? (
              <Chip
                label={`${activeSshConfig.username}@${activeSshConfig.host}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            ) : activeConnectionType === 'rclone' ? (
              <Chip
                label={t('wizard.location.rcloneRemoteLabel')}
                size="small"
                color="primary"
                variant="outlined"
              />
            ) : isInsideLocalMount && activeConnectionType === 'local' ? (
              <Chip
                icon={<HardDrive size={14} />}
                label={t('fileExplorer.chips.host')}
                size="small"
                color="primary"
                variant="outlined"
              />
            ) : null}
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {/* Breadcrumb Navigation */}
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: 'background.default',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Breadcrumbs
              separator={<ChevronRight size={12} />}
              maxItems={6}
              sx={{ '& .MuiBreadcrumbs-separator': { mx: 0.25 } }}
            >
              {getBreadcrumbs().map((crumb, index) => (
                <Link
                  key={index}
                  component="button"
                  variant="caption"
                  onClick={() => handleBreadcrumbClick(crumb.path)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    textDecoration: 'none',
                    color: 'text.primary',
                    fontWeight: index === getBreadcrumbs().length - 1 ? 600 : 400,
                    '&:hover': {
                      textDecoration: 'underline',
                      color: 'primary.main',
                    },
                  }}
                >
                  {index === 0 && <Home size={12} />}
                  {crumb.label}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>

          {/* Search and Create Folder */}
          <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('fileExplorer.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              inputRef={searchInputRef}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                },
                '& .MuiOutlinedInput-input': {
                  py: 0.75,
                },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            {activeConnectionType !== 'agent' && activeConnectionType !== 'rclone' && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<FolderPlus size={16} />}
                onClick={() => setShowCreateFolder(true)}
                sx={{
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  height: '35px',
                  minHeight: '35px',
                }}
              >
                {t('fileExplorer.newFolder')}
              </Button>
            )}
          </Box>

          {/* Error Display */}
          {error && (
            <Box sx={{ px: 2, pb: 1 }}>
              <Alert severity="error" sx={{ borderRadius: 1, py: 0.5 }}>
                {error}
              </Alert>
            </Box>
          )}

          {/* Mount Point Info */}
          {currentPath === '/' && activeConnectionType === 'local' && sshConnections.length > 0 && (
            <Box sx={{ px: 2, pb: 1 }}>
              <Alert severity="info" sx={{ borderRadius: 1, py: 0.5 }}>
                <Typography variant="caption">{t('fileExplorer.sshInfoAlert')}</Typography>
              </Alert>
            </Box>
          )}

          {/* Loading State */}
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                flex: 1,
                minHeight: 0,
              }}
            >
              <CircularProgress size={32} />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mt: 1.5,
                }}
              >
                {t('fileExplorer.loading')}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* File List */}
              <List
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  overscrollBehavior: 'contain',
                  WebkitOverflowScrolling: 'touch',
                  px: 0.5,
                  py: 0,
                }}
              >
                {filteredItems.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 4,
                      color: 'text.secondary',
                    }}
                  >
                    <FolderOpenIcon sx={{ fontSize: 36 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        mt: 1.5,
                      }}
                    >
                      {t('fileExplorer.noItemsFound')}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.disabled',
                      }}
                    >
                      {searchTerm
                        ? t('fileExplorer.tryDifferentSearch')
                        : t('fileExplorer.emptyDirectory')}
                    </Typography>
                  </Box>
                ) : (
                  filteredItems.map((item) => {
                    const isSelectable =
                      (selectMode === 'directories' && item.is_directory) ||
                      (selectMode === 'files' && !item.is_directory) ||
                      selectMode === 'both'

                    const isSelected = selectedPaths.includes(item.path)

                    return (
                      <ListItem
                        key={item.path}
                        disablePadding
                        secondaryAction={
                          isSelectable && multiSelect ? (
                            <Checkbox
                              edge="end"
                              checked={isSelected}
                              onChange={() => handleItemSelect(item)}
                              size="small"
                            />
                          ) : null
                        }
                      >
                        <ListItemButton
                          onClick={() => {
                            if (item.is_directory) {
                              if (item.is_mount_point && item.ssh_connection) {
                                const sshCfg = {
                                  ssh_key_id: item.ssh_connection.ssh_key_id,
                                  host: item.ssh_connection.host,
                                  username: item.ssh_connection.username,
                                  port: item.ssh_connection.port,
                                }
                                const startPath = item.ssh_connection.default_path || '/'
                                loadDirectory(startPath, 'ssh', sshCfg)
                              } else {
                                loadDirectory(item.path)
                              }

                              setSearchTerm('')
                              setShouldFocusSearchInput(true)
                              return
                            }

                            if (isSelectable) {
                              handleItemSelect(item)
                            }
                          }}
                          selected={isSelected && !multiSelect}
                          sx={{
                            py: 0.5,
                            px: 1.5,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                            '&.Mui-selected': {
                              bgcolor: 'primary.50',
                              '&:hover': {
                                bgcolor: 'primary.100',
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {item.is_mount_point ? (
                              <HardDrive size={18} color="#10b981" />
                            ) : item.is_local_mount ? (
                              <HardDrive size={18} color="#6366f1" />
                            ) : item.is_borg_repo ? (
                              <Archive size={18} color="#ff6b6b" />
                            ) : item.is_directory ? (
                              <FolderOpenIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                            ) : (
                              <File size={18} color="#999" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                }}
                              >
                                <Typography variant="body2">{item.name}</Typography>
                                {item.is_mount_point && (
                                  <Chip
                                    label={t('fileExplorer.chips.remote')}
                                    size="small"
                                    color="success"
                                    sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600 }}
                                  />
                                )}
                                {item.is_local_mount && (
                                  <Chip
                                    label={t('fileExplorer.chips.host')}
                                    size="small"
                                    color="primary"
                                    sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600 }}
                                  />
                                )}
                                {item.is_borg_repo && (
                                  <Chip
                                    label={t('fileExplorer.chips.borg')}
                                    size="small"
                                    color="warning"
                                    sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600 }}
                                  />
                                )}
                                {!item.is_directory && item.size && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.disabled',
                                      ml: 'auto',
                                    }}
                                  >
                                    {formatFileSize(item.size)}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    )
                  })
                )}
              </List>

              {/* Info Box */}
              {multiSelect && selectedPaths.length > 0 && (
                <Box
                  sx={{
                    flexShrink: 0,
                    px: 2,
                    py: 1,
                    bgcolor: 'primary.50',
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                    }}
                  >
                    {t('fileExplorer.selectedCount', { count: selectedPaths.length })}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
            {t('fileExplorer.cancel')}
          </Button>
          <Box sx={{ flex: 1 }} />
          {selectMode === 'directories' && (
            <Button onClick={handleSelectCurrent} variant="outlined" size="small" sx={{ mr: 1 }}>
              {t('fileExplorer.useCurrent')}
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            variant="contained"
            size="small"
            disabled={selectedPaths.length === 0}
          >
            {multiSelect && selectedPaths.length > 0
              ? t('fileExplorer.selectWithCount', { count: selectedPaths.length })
              : t('fileExplorer.select')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog
        open={showCreateFolder}
        onClose={() => !creatingFolder && setShowCreateFolder(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('fileExplorer.createFolderTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('fileExplorer.folderNameLabel')}
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newFolderName.trim()) {
                handleCreateFolder()
              }
            }}
            placeholder={t('fileExplorer.folderNamePlaceholder')}
            margin="dense"
            disabled={creatingFolder}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setShowCreateFolder(false)
              setNewFolderName('')
              setError(null)
            }}
            disabled={creatingFolder}
          >
            {t('fileExplorer.cancel')}
          </Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            disabled={!newFolderName.trim() || creatingFolder}
          >
            {creatingFolder ? t('fileExplorer.creating') : t('fileExplorer.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
